import { MemberFormData } from '../types/member.types';

import {
  isHalfOrFullWidthNumeric,
  isHalfWidthKatakana,
  isHalfWidthNumeric,
  isValidEmail,
  isValidNoharaEmail,
  ValidationError,
} from '@/features/companies/utils/validations';
import { FieldValidationResult } from '@/features/companies';
import { VALIDATION_MESSAGES } from '@/features/companies/utils/validations/validation-messages';

const addValidationError = (
  errors: ValidationError[],
  result: FieldValidationResult,
  fieldName: string
): void => {
  if (!result.isValid) {
    errors.push({ field: fieldName, message: result.error });
  }
};

const validateFamilyName = (value: string): FieldValidationResult => {
  if (!value.trim()) {
    return { isValid: false, error: VALIDATION_MESSAGES.MEMBER_LAST_NAME_REQUIRED };
  }
  return { isValid: true };
};

const validateFirstName = (name: string): FieldValidationResult => {
  if (!name.trim()) {
    return { isValid: false, error: VALIDATION_MESSAGES.MEMBER_FIRST_NAME_REQUIRED };
  }
  return { isValid: true };
};

const validateKatana = (value: string, message: string): FieldValidationResult => {
  if (value.trim() && !isHalfWidthKatakana(value)) {
    return { isValid: false, error: message };
  }
  return { isValid: true };
};

const validateDepartmentCode = (code: string): FieldValidationResult => {
  if (!code.trim()) {
    return { isValid: false, error: VALIDATION_MESSAGES.MEMBER_DEPARTMENT_REQUIRED };
  }

  return { isValid: true };
};

const validateAuthorities = (authorities: string[]): FieldValidationResult => {
  if (authorities.length === 0) {
    return { isValid: false, error: VALIDATION_MESSAGES.MEMBER_AUTHORITIES_REQUIRED };
  }

  return { isValid: true };
};

const validateIsSelectable = (isSelectable: string): FieldValidationResult => {
  if (!isSelectable.trim()) {
    return { isValid: false, error: VALIDATION_MESSAGES.MEMBER_IS_SELECTABLE_REQUIRED };
  }

  return { isValid: true };
};

const validateEmail = (email: string): FieldValidationResult => {
  const trimmedEmail = email.trim();
  if (!trimmedEmail) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.MEMBER_EMAIL_REQUIRED,
    };
  }

  if (!isValidEmail(trimmedEmail)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.MEMBER_EMAIL_INVALID,
    };
  }

  if (!isValidNoharaEmail(trimmedEmail)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.MEMBER_EMAIL_DOMAIN_RESTRICTED,
    };
  }

  return { isValid: true };
};

const validatePhone = (tel: string): FieldValidationResult => {
  if (tel.trim() && !isHalfOrFullWidthNumeric(tel)) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.MEMBER_PHONE_FORMAT_HYPHEN_NUMERIC,
    };
  }
  return { isValid: true };
};

const validateCode = (code: string): FieldValidationResult => {
  if (!code.trim()) {
    return { isValid: false, error: VALIDATION_MESSAGES.MEMBER_CODE_REQUIRED };
  }
  if (!isHalfWidthNumeric(code.trim()) || code.length > 9) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.MEMBER_CODE_NUMERIC_ONLY,
    };
  }

  if (code.trim() && Number(code.trim()) === 0) {
    return {
      isValid: false,
      error: VALIDATION_MESSAGES.MEMBER_CODE_NOT_ZERO,
    };
  }

  return { isValid: true };
};

const validateBasicInformation = (form: MemberFormData, errors: ValidationError[]): void => {
  // Basic Information Section Validations
  addValidationError(errors, validateCode(form.code || ''), 'code');
  addValidationError(errors, validateFamilyName(form.lastName || ''), 'lastName');
  addValidationError(errors, validateFirstName(form.firstName || ''), 'firstName');
  addValidationError(
    errors,
    validateKatana(form.lastNameYomi || '', VALIDATION_MESSAGES.MEMBER_LAST_NAME_KANA_FORMAT),
    'lastNameYomi'
  );
  addValidationError(
    errors,
    validateKatana(form.firstNameYomi || '', VALIDATION_MESSAGES.MEMBER_FIRST_NAME_KANA_FORMAT),
    'firstNameYomi'
  );
  addValidationError(errors, validatePhone(form.tel || ''), 'tel');

  addValidationError(errors, validateIsSelectable(form.isSelectable || ''), 'isSelectable');
  addValidationError(errors, validateAuthorities(form.authorities), 'authorities');

  // Department Code Validation
  addValidationError(errors, validateDepartmentCode(form.bucode || ''), 'bucode');

  //email validation
  addValidationError(errors, validateEmail(form.email || ''), 'email');
};

export const validateMemberForm = (
  form: MemberFormData
): { isValid: boolean; errors: ValidationError[] } => {
  const errors: ValidationError[] = [];

  validateBasicInformation(form, errors);

  return {
    isValid: errors.length === 0,
    errors,
  };
};
